import React, { useContext } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import { Link } from 'gatsby'
import ThemeContext from '@/context/Theme'
import { zIndex, easing, layouts, lang, fonts } from '@/utils/preset'
import { tablet, mobile } from '@/utils/media'
import { mq, rem } from '@/utils/helpers'
import Logo from '@/components/svg/Logo'

export default function Nav (props) {
  const { homeLink, selectedLang, intl } = lang(props.lang)
  const langKey = selectedLang.langKey
  const { theme } = useContext(ThemeContext)
  return (
    <Root className={props.show ? `is-shown` : `is-hidden`}>
      <Head>
        <Col>
          <LogoButton to={homeLink}>
            <Logo color="dark" />
          </LogoButton>
        </Col>
        <Col>
          <CloseButton theme={theme} onClick={props.onClose}>
            <CloseButton_Line />
            <CloseButton_Line />
          </CloseButton>
        </Col>
      </Head>
      <Container>
        <Main>
          <Main_Content>
            <List>
              <List_Item>
                <Item>
                  <Item_Link to={`${selectedLang.link}#top`}>
                    <Item_Main>TOP</Item_Main>
                  </Item_Link>
                  <Item_Children>
                    <Item_Child>
                      <SubItem to={`${selectedLang.link}#main-message`}>
                        MAIN MESSAGE
                      </SubItem>
                    </Item_Child>
                    <Item_Child>
                      <SubItem to={`${selectedLang.link}#movie`}>
                        VISION MOVIE
                      </SubItem>
                    </Item_Child>
                    <Item_Child>
                      <SubItem to={`${selectedLang.link}#book`}>
                        VISION BOOK
                      </SubItem>
                    </Item_Child>
                    <Item_Child>
                      <SubItem to={`${selectedLang.link}#statement`}>
                        VISION STATEMENT
                      </SubItem>
                    </Item_Child>
                    <Item_Child>
                      <SubItem to={`${selectedLang.link}#brand`}>
                        BRAND STRUCTURE
                      </SubItem>
                    </Item_Child>
                  </Item_Children>
                </Item>
              </List_Item>
              <List_Divider />
              <List_Item>
                <Item>
                  <Item_Link to={`${selectedLang.link}/conversation`}>
                    <Item_Main>SPECIAL CONVERSATION</Item_Main>
                    <Item_Sub>“TADAHIKO KONOIKE × YOSHIAKI SAKITO” </Item_Sub>
                  </Item_Link>
                </Item>
              </List_Item>
            </List>
            <Button theme={theme} href={`https://www.konoike.net${'/'.replace('/', selectedLang.link)}`} target="_blank">
              <Button_Content>
                <Button_Icon width="1269" height="1024" viewBox="0 0 1269 1024" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                  <path d="M114.224 240.75H0V936.972C0 984.838 51.4008 1024 114.224 1024H1028.02V936.972H114.224V240.75Z"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M199.828 131.965C199.828 50.4425 278.776 0 353.55 0H1115.04C1189.82 0 1268.77 50.4425 1268.77 131.965V741.16C1268.77 822.682 1189.82 873.125 1115.04 873.125H353.55C278.776 873.125 199.828 822.682 199.828 741.16V131.965ZM353.55 89.8749C308.661 89.8749 289.703 117.757 289.703 131.965V741.16C289.703 755.368 308.661 783.25 353.55 783.25H1115.04C1159.93 783.25 1178.89 755.368 1178.89 741.16V131.965C1178.89 117.757 1159.93 89.8749 1115.04 89.8749H353.55Z"/>
                </Button_Icon>
                <Button_Text>{intl('Corporate Site', langKey)}</Button_Text>
              </Button_Content>
            </Button>
          </Main_Content>
        </Main>
      </Container>
    </Root>
  )
}

const anim = {
  button: {
    color: (color1, color2) => keyframes({
      '50%': {
        color: color1
      },
      '100%': {
        color: color2
      },
      label: 'button-color'
    }),
    before: keyframes({
      '50%': {
        transform: 'translateX(100%)'
      },
      '100%': {
        transform: 'translateX(100%)'
      },
      label: 'button-after'
    }),
    after: keyframes({
      '0%': {
        transform: 'translateX(-100%)'
      },
      '50%': {
        transform: 'translateX(-100%)'
      },
      '100%': {
        transform: 'translateX(0%)'
      },
      label: 'button-after'
    })
  },
  line: {
    before: keyframes({
      '0%': {
        transform: 'translateX(0%)'
      },
      '50%': {
        transform: 'translateX(100%)'
      },
      '100%': {
        transform: 'translateX(100%)'
      },
      label: 'line-before'
    }),
    after: (x) => keyframes({
      '0%': {
        transform: 'translateX(-100%)'
      },
      '50%': {
        transform: 'translateX(-100%)'
      },
      '100%': {
        transform: `translateX(${x})`
      },
      label: 'line-after'
    })
  },
  linkLine: keyframes({
    '0%': {
      transform: 'translateX(-110%)'
    },
    '75%': {
      transform: 'translateX(110%)'
    },
    '100%': {
      transform: 'translateX(110%)'
    }
  })
}

const Container = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${rem(20)} ${rem(20)} 0;
  background-image: radial-gradient(circle at left top, rgba(65,171,64,1) 50%, rgba(17,119,63,1) 100%);
  @media ${mq.and(tablet)} {
    padding: ${rem(10)} ${rem(layouts.defs.pad.tablet / 2)} 0;
  }
  @media ${mq.and(mobile)} {
    padding: 10px ${rem(layouts.defs.pad.mobile / 2)} 0;
  }
  transition:
    transform 0.4s ${easing.css.easeInOutCirc},
    opacity 0.4s ${easing.css.easeInOutCirc};
`

const Head = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  width: 100%;
  padding: ${rem(20)};
  transition:
    transform 0.4s ${easing.css.easeInOutCirc},
    opacity 0.4s ${easing.css.easeInOutCirc};

  @media ${mq.and(tablet)} {
    padding: ${rem(10)} ${rem(layouts.defs.pad.tablet / 2)};
  }

  @media ${mq.and(mobile)} {
    padding: ${rem(10)} ${rem(layouts.defs.pad.mobile / 2)};
  }
`

const Col = styled.div`
  display: flex;
  align-items: center;
`

const LogoButton = styled(Link)`
  display: block;
  width: ${rem(192)};
  @media ${mq.and(tablet, mobile)} {
    width: ${rem(80)};
  }
`

const Main = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 ${rem(80)};
  @media ${mq.and(tablet)} {
    padding: 0 ${rem(layouts.defs.pad.tablet / 2)} 0;
  }
  @media ${mq.and(mobile)} {
    padding: 0 ${rem(layouts.defs.pad.mobile / 2)} 0;
  }
`

const Main_Content = styled.div`
  padding: ${rem(64)} 0;
  transition: transform 0.4s ease-in-out;
  @media ${mq.and(mobile)} {
    padding: ${rem(40)} 0;
  }
`

const Button = styled.a`
  margin: ${rem(80)} auto 0;
  @media ${mq.and(tablet, mobile)} {
    margin-top: ${rem(48)};
  }
  position: relative;
  display: block;
  width: ${rem(230)};
  height: ${rem(50)};
  @media ${mq.and(mobile)} {
    width: 100%;
    max-width: ${rem(230)};
  }
  color: ${props => props.theme.text.contrast.base};
  background-color: ${props => props.theme.text.contrast.base};;
  overflow: hidden;
  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.theme.text.base};
  }
  &:after {
    transform: translateX(-100%);
  }
  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      animation: ${props => anim.button.color(props.theme.text.base, props.theme.text.contrast.base)} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:before {
      animation: ${anim.button.before} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:after {
      animation: ${anim.button.after} 0.5s ${easing.css.easeInOutExpo};
    }
  }
`

const Button_Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: ${rem(14)};
`

const Button_Icon = styled.svg`
  width: ${rem(19.8281249992)};
  height: ${rem(16)};
`

const Button_Text = styled.span`
  margin-left: ${rem(14)};
  transform: translateY(-10%);
`

const CloseButton_Line = styled.div`
  position: absolute;
  top: 50%;
  left: ${rem(18)};
  right: ${rem(18)};
  @media ${mq.and(tablet, mobile)} {
    left: ${rem(9)};
    right: ${rem(9)};
  }
  transform-origin: center;
  z-index: 2;
  height: 2px;
  overflow: hidden;
  &:nth-of-type(1) {
    transform: rotate(45deg);
  }
  &:nth-of-type(2) {
    transform: rotate(-45deg);
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: currentColor;
    transform: translateX(0%);
  }
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: currentColor;
    transform: translateX(-100%);
    transform-origin: right center;
  }
`

const CloseButton = styled.div`
  cursor: pointer;
  position: relative;
  margin-left: ${rem(30)};
  width: ${rem(66)};
  height: ${rem(50)};
  background-color: ${props => props.theme.text.base};
  color: ${props => props.theme.text.base};
  padding: ${rem(14)} ${rem(18)};
  overflow: hidden;
  @media ${mq.and(tablet, mobile)} {
    width: ${rem(37)};
    height: ${rem(28)};
    padding: ${rem(8)} ${rem(10)};
  }

  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.theme.text.contrast.base};
  }
  &:after {
    transform: translateX(-100%);
  }
  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      animation: ${props => anim.button.color(props.theme.text.contrast.base, props.theme.text.base)} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:before {
      animation: ${anim.button.before} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:after {
      animation: ${anim.button.after} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover ${CloseButton_Line} {
      &:nth-of-type(1):before {
        animation: ${anim.line.before} 0.5s ${easing.css.easeInOutExpo};
        animation-fill-mode: forwards;
      }
      &:nth-of-type(2):before {
        animation: ${anim.line.before} 0.5s ${easing.css.easeInOutExpo} 0.05s;
        animation-fill-mode: forwards;
      }
      &:nth-of-type(1):after {
        animation: ${anim.line.after('0%')} 0.5s ${easing.css.easeInOutExpo};
        animation-fill-mode: forwards;
      }
      &:nth-of-type(2):after {
        animation: ${anim.line.after('0%')} 0.5s ${easing.css.easeInOutExpo} 0.05s;
        animation-fill-mode: forwards;
      }
    }
  }
`

const List = styled.div``

const List_Item = styled.div`
  text-align: center;
  flex-direction: column;
  justify-content: center;
`

const List_Divider = styled.hr`
  margin: ${rem(40)} auto;
  max-width: ${rem(650)};
  border-top: 1px solid #fff;

  @media ${mq.and(mobile)} {
    margin: ${rem(24)} auto;
  }
`

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Item_Link = styled(Link)`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  flex-direction: column;
  color: #fff;
  padding-bottom: ${rem(4)};

  &::before {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0;
    display: block;
    width: 100%;
    height: 2px;
    background-color: currentColor;
    transform: translateX(-110%);
  }

  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover::before {
      animation: ${anim.linkLine} .75s ${easing.css.easeInOutExpo} infinite;
    }
  }
`

const Item_Main = styled.div`
  font-family: ${fonts.base.en};
  font-weight: 700;
  font-size: ${rem(42)};
  letter-spacing: 0.04em;
  line-height: 1.25;

  @media ${mq.and(mobile)} {
    font-size: ${rem(32)};
  }
`

const Item_Sub = styled.div`
  margin-top: ${rem(8)};
  padding-bottom: ${rem(8)};
  font-size: ${rem(14)};
  letter-spacing: 0.1em;
`

const Item_Children = styled.div`
`

const Item_Child = styled.div`
  margin-top: ${rem(24)};
  text-align: center;

  @media ${mq.and(mobile)} {
    margin-top: ${rem(16)};
  }
`

const SubItem = styled(Link)`
  position: relative;
  display: inline-block;
  overflow: hidden;
  color: #fff;
  font-family: ${fonts.base.en};
  font-weight: 700;
  font-size: ${rem(20)};
  padding-bottom: ${rem(6)};

  @media ${mq.and(mobile)} {
    font-size: ${rem(16)};
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0;
    display: block;
    width: 100%;
    height: 2px;
    background-color: currentColor;
    transform: translateX(-110%);
  }

  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover::before {
      animation: ${anim.linkLine} .75s ${easing.css.easeInOutExpo} infinite;
    }
  }
`

const Root = styled.nav`
  position: fixed;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #fff;
  z-index: ${zIndex.header.nav};
  transition: transform 0.4s ${easing.css.easeInOutExpo};

  &.is-hidden {
    transform: translateY(100%);
    transition-delay: 0.1s;

    ${Head} {
      opacity: 0;
    }

    ${Container} {
      opacity: 0;
    }

    ${Main_Content} {
      transform: translateY(60px);
    }
  }
  &.is-shown {
    transform: translateY(0%);

    ${Head} {
      opacity: 1;
    }

    ${Container} {
      opacity: 1;
    }

    ${Main_Content} {
      transform: translateY(0);
      transition-delay: 0.1s;
    }
  }
`
